import React, { useEffect, useState } from 'react'
import { Box, Modal } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { navigate } from '@reach/router'
import {
  ROUTE_CREATE,
  ROUTE_FORM_GENERATOR,
  ROUTE_FORM_GENERATOR_FORM,
} from '../../routes/routes-constants'
import { getFormContainer } from '../../container/form-module'
import { FORM_SERVICE_KEY, USER_FORM_SERVICE_KEY } from '../../modules/forms'
import { FormService } from '../../modules/forms/services/FormService'
import { Form } from '../../modules/forms/models/Form'
import { Query, QueryParam } from '../../common/api/Query'
import styles from './Table.module.css'
import { emptyList, ItemList } from '../../common/models/ItemList'
import { AppButton, ButtonTheme } from '../../components/app-button/AppButton'
import { AppTable, Field } from '../../components/table'
import { Actions, Pager } from '../../components/table/types'
import { UserFormService } from '../../modules/forms/services/UserFormService'
import { UserForm } from '../../modules/forms/models/UserForm'
import createNewForm from '../../assets/resource_icons/ico-form-crear.svg'
import assignIcon from '../../assets/table_icons/assign-form.svg'
import seeIcon from '../../assets/table_icons/view.svg'
import editIcon from '../../assets/table_icons/edit.svg'
import deleteIcon from '../../assets/table_icons/delete.svg'
import downloadIcon from '../../assets/table_icons/download.svg'
import { forkJoin, Observable } from 'rxjs'
import { reduceString } from '../../common/utils/strings'
import { IStatusService } from '../../common/status/StatusService'
import { getAppContainer, STATUS_SERVICE_KEY } from '../../container/app'
import { getCategoryLabel } from '../../common/enums/FormCategories'
import { PREDEFINED_FORM_IDS } from '../../common/enums/PredefinedForms'
import { Pagination } from '../../components/table/Pagination'
import { PageLayout } from 'pages/layout'
import { DownloadModal } from './DownloadModal'
import { CustomModal } from '../../components/modal/CustomModal'

const formContainer = getFormContainer()
const formService = formContainer.get<FormService>(FORM_SERVICE_KEY)
const userFormService = formContainer.get<UserFormService>(USER_FORM_SERVICE_KEY)

export function Table() {
  const { t } = useTranslation()
  const statusService = getAppContainer().get<IStatusService>(STATUS_SERVICE_KEY)
  const [forms, setForms] = useState<ItemList<Form>>(emptyList<Form>())
  const [count, setCount] = useState<number>(0)
  const [page, setPage] = useState<number>(0)
  const [pager, setPager] = useState<Pager>()
  const [formsPerPage, setFormsPerPage] = useState<number>(10)
  const [userForms, setUserForms] = useState<string[]>([])
  const [downloadModalOpen, setDownloadModalOpen] = useState<boolean>(false)
  const [selectedFormForDownload, setSelectedFormForDownload] = useState<Form | null>(null)
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false)
  const [selectedFormForDelete, setSelectedFormForDelete] = useState<Form | null>(null)

  const getUserForms = (ids: string[]): Observable<UserForm[]> =>
    forkJoin(
      ids.map((id) =>
        userFormService.getFilteredList(
          new Query({
            query: [new QueryParam<UserForm>('formID', id)],
          })
        )
      )
    ) as unknown as Observable<UserForm[]>

  useEffect(() => {
    formService
      .getFilteredList(
        new Query({
          pager: { offset: page * formsPerPage, limit: formsPerPage },
          sort: [{ field: 'title' }],
        })
      )
      .subscribe((res) => {
        setForms(res)
        setCount(res.count)

        getUserForms(res.items.map((f) => f.id || '')).subscribe((res) =>
          setUserForms(res.map((uf) => uf.id || ''))
        )
      })
  }, [page, formsPerPage])

  const createForm = () => navigate(`${ROUTE_FORM_GENERATOR_FORM}/${ROUTE_CREATE}`)
  const editForm = (form: Form) => navigate(`${ROUTE_FORM_GENERATOR_FORM}/${form.id}`)

  const handleDeleteClick = (form: Form) => {
    setSelectedFormForDelete(form)
    setOpenDeleteModal(true)
  }

  const handleCloseDeleteModal = () => {
    setOpenDeleteModal(false)
    setSelectedFormForDelete(null)
  }

  const handleDeleteForm = () => {
    if (!selectedFormForDelete?.id) return

    formService.delete(selectedFormForDelete.id).subscribe(() => {
      setForms((prevForms) => ({
        ...prevForms,
        items: prevForms.items.filter((f) => f.id !== selectedFormForDelete.id),
        count: prevForms.count - 1,
      }))
      statusService.sendStatus({ variant: 'success' })
      setOpenDeleteModal(false)
      setSelectedFormForDelete(null)
    })
  }

  const handlePaginationChange = (e: unknown, value: number) => setPage(value)

  const resultForm = (form: Form) => navigate(`${ROUTE_FORM_GENERATOR_FORM}/${form.id}/results`)

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = Number.parseInt(event.target.value) || 10
    setFormsPerPage(newValue)
    setPage(0)
  }

  const associateForm = (form: Form) => navigate(`${ROUTE_FORM_GENERATOR}/${form.id}/assignTo`)

  const handleDownloadClick = (form: Form) => {
    if (!form.id) return
    setSelectedFormForDownload(form)
    setDownloadModalOpen(true)
  }

  const hideForm = (form: Form) => (form.id ? userForms.includes(form.id) : false)

  const isPredefinedForm = (form: Form): boolean =>
    form.id ? PREDEFINED_FORM_IDS.includes(form.id) : false

  const shouldHideEditButton = (form: Form): boolean => isPredefinedForm(form) || hideForm(form)
  const shouldHideDeleteButton = (form: Form): boolean => isPredefinedForm(form) || hideForm(form)
  const shouldHideDownloadButton = (form: Form): boolean => !form.id

  useEffect(() => {
    setPager({
      page,
      count,
      handleChangePage: handlePaginationChange,
      rowsPerPage: formsPerPage,
      handleChangeRowsPerPage,
    })
  }, [page, count, formsPerPage])

  const fields: Field<Form>[] = [
    {
      name: 'title',
      label: t('title'),
      renderFunc: (f, form) => t(form.title),
    },
    {
      name: 'description',
      label: t('description'),
      renderFunc: (f, form) => reduceString(t(form.description ?? ''), 60),
    },
    {
      name: 'category',
      label: t('category'),
      renderFunc: (f, form) => t(getCategoryLabel(form.category)),
    },
  ]

  const actions: Actions<Form> = {
    actionsColumn: t('actions'),
    preserveHiddenSpace: true,
    items: [
      {
        handler: resultForm,
        icon: seeIcon,
        label: 'see',
      },
      {
        handler: editForm,
        icon: editIcon,
        label: 'edit',
        hidden: shouldHideEditButton,
      },
      {
        handler: handleDownloadClick,
        icon: downloadIcon,
        label: 'download',
        hidden: shouldHideDownloadButton,
      },
      {
        handler: associateForm,
        icon: assignIcon,
        label: 'add',
      },
      {
        handler: handleDeleteClick,
        icon: deleteIcon,
        label: 'delete',
        hidden: shouldHideDeleteButton,
      },
    ],
  }

  return (
    <PageLayout>
      <Modal open={openDeleteModal} onClose={handleCloseDeleteModal}>
        <CustomModal
          title={t('deleteForm')}
          warningText={t('irreversibleFormAction')}
          handleClose={handleCloseDeleteModal}
          handleSave={handleDeleteForm}
        />
      </Modal>

      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
        <Box
          className={styles.buttonContainer}
          sx={{ flexShrink: 0, display: 'flex', justifyContent: 'flex-end' }}
        >
          <AppButton
            theme={ButtonTheme.NewPrimary}
            type={'button'}
            label={t('createTemplateForm')}
            startIcon={createNewForm}
            handler={createForm}
          />
        </Box>

        {forms.items.length > 0 ? (
          <>
            <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
              <AppTable items={forms.items} rowKeyField="id" fields={fields} actions={actions} />
            </Box>
            {pager && (
              <Box sx={{ flexShrink: 0, mt: 2, display: 'flex', justifyContent: 'flex-start' }}>
                <Pagination {...pager} />
              </Box>
            )}
          </>
        ) : (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <h1>{t('noForms')}</h1>
          </Box>
        )}
      </Box>

      <DownloadModal
        open={downloadModalOpen}
        form={selectedFormForDownload}
        onClose={() => {
          setDownloadModalOpen(false)
          setSelectedFormForDownload(null)
        }}
      />
    </PageLayout>
  )
}
